import React, { useState, useMemo, useEffect, useCallback } from 'react'
import { Users, Search, ChevronRight, CheckCircle, AlertCircle, X, FileSpreadsheet, Layers, RefreshCw, Loader2, Trash2 } from 'lucide-react'
import ImportStudentsModal from './ImportStudentsModal'
import { API_CONFIG } from '../../config/api.config'

const API = API_CONFIG.python.apiUrl
const getToken = () =>
  localStorage.getItem('jwt_token') || localStorage.getItem('auth_token') || localStorage.getItem('token') || ''

interface StudentItem {
  id: string | number
  studentName: string
  studentEmail: string
  phone?: string
  school?: string
  className?: string
  status: 'linked' | 'invited'
}

// Quản lý lớp & học sinh trong ebook — DÙNG CHUNG endpoint /students với PMaker (DB chung).
const StudentManager: React.FC = () => {
  const [selectedClassName, setSelectedClassName] = useState<string | null>(null)
  const [isImportModalOpen, setIsImportModalOpen] = useState(false)
  const [loading, setLoading] = useState(true)
  const [allStudents, setAllStudents] = useState<StudentItem[]>([])
  const [error, setAppError] = useState<string | null>(null)
  const [confirmModal, setConfirmModal] = useState<{ isOpen: boolean; type: 'confirm' | 'success' | 'error'; title: string; message: string; action?: () => void }>({ isOpen: false, type: 'confirm', title: '', message: '' })

  const loadData = useCallback(async () => {
    if (!getToken()) { setAppError('Bạn cần đăng nhập để xem danh sách lớp.'); setLoading(false); return }
    setLoading(true); setAppError(null)
    try {
      const res = await fetch(`${API}/students/teacher/students`, { headers: { Authorization: `Bearer ${getToken()}` } })
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || `Lỗi server (${res.status})`) }
      const data = await res.json()
      setAllStudents(data || [])
      if (data && data.length > 0) {
        const uniq = Array.from(new Set(data.map((s: any) => s.className || 'Lớp chưa phân loại'))) as string[]
        setSelectedClassName((cur) => cur || uniq[0] || null)
      }
    } catch (e: any) { setAppError(e.message) } finally { setLoading(false) }
  }, [])

  useEffect(() => { loadData() }, [loadData])

  const classes = useMemo(() => {
    const m = new Map<string, number>()
    allStudents.forEach((s) => { const n = s.className || 'Lớp chưa phân loại'; m.set(n, (m.get(n) || 0) + 1) })
    return Array.from(m.entries()).map(([name, count]) => ({ id: name, name, students: count }))
  }, [allStudents])

  const activeClass = useMemo(() => classes.find((c) => c.id === selectedClassName), [classes, selectedClassName])
  const filteredStudents = useMemo(
    () => (!selectedClassName ? [] : allStudents.filter((s) => (s.className || 'Lớp chưa phân loại') === selectedClassName)),
    [allStudents, selectedClassName]
  )

  const deleteClass = (e: React.MouseEvent, className: string) => {
    e.stopPropagation()
    setConfirmModal({
      isOpen: true, type: 'confirm', title: 'Xác nhận xoá lớp',
      message: `Xoá toàn bộ lớp "${className}"? Hành động này xoá học sinh trong database (HS đã liên kết vẫn giữ tài khoản) và không thể hoàn tác.`,
      action: async () => {
        try {
          const res = await fetch(`${API}/students/teacher/classes/${encodeURIComponent(className)}`, { method: 'DELETE', headers: { Authorization: `Bearer ${getToken()}` } })
          if (res.ok) {
            setConfirmModal({ isOpen: true, type: 'success', title: 'Thành công', message: `Đã xoá lớp ${className}.` })
            if (selectedClassName === className) setSelectedClassName(null)
            loadData()
          } else { const er = await res.json().catch(() => ({})); setConfirmModal({ isOpen: true, type: 'error', title: 'Lỗi', message: er.detail || 'Không thể xoá lớp' }) }
        } catch { setConfirmModal({ isOpen: true, type: 'error', title: 'Lỗi', message: 'Lỗi kết nối server' }) }
      },
    })
  }

  return (
    <div className="flex flex-col lg:flex-row gap-6 mt-6">
      {confirmModal.isOpen && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-3xl shadow-2xl max-w-sm w-full overflow-hidden border border-gray-100">
            <div className="p-8 text-center">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6 ${confirmModal.type === 'confirm' ? 'bg-amber-50 text-amber-500' : confirmModal.type === 'success' ? 'bg-emerald-50 text-emerald-500' : 'bg-red-50 text-red-500'}`}>
                {confirmModal.type === 'confirm' && <AlertCircle size={32} />}
                {confirmModal.type === 'success' && <CheckCircle size={32} />}
                {confirmModal.type === 'error' && <X size={32} />}
              </div>
              <h3 className="text-xl font-black text-gray-900 mb-2">{confirmModal.title}</h3>
              <p className="text-gray-500 text-sm font-medium leading-relaxed mb-8">{confirmModal.message}</p>
              <div className="flex gap-3">
                {confirmModal.type === 'confirm' ? (
                  <>
                    <button onClick={() => setConfirmModal({ ...confirmModal, isOpen: false })} className="flex-1 px-4 py-3 bg-gray-50 hover:bg-gray-100 text-gray-600 rounded-2xl font-bold text-sm">Hủy bỏ</button>
                    <button onClick={() => { confirmModal.action?.(); }} className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-2xl font-bold text-sm shadow-lg shadow-red-200">Xác nhận xoá</button>
                  </>
                ) : (
                  <button onClick={() => setConfirmModal({ ...confirmModal, isOpen: false })} className="w-full px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-2xl font-bold text-sm">Đóng lại</button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sidebar lớp */}
      <div className="w-full lg:w-64 shrink-0 flex flex-col gap-3">
        <button onClick={() => setIsImportModalOpen(true)} className="flex items-center justify-center gap-2 px-4 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-2xl font-bold text-sm shadow-md shadow-emerald-500/20">
          <FileSpreadsheet size={18} /> Nhập lớp (Excel)
        </button>
        <div className="bg-white border border-gray-200 rounded-3xl shadow-sm overflow-hidden flex-1 flex flex-col">
          <div className="p-3 border-b border-gray-100 bg-gray-50/50 flex items-center gap-2">
            <span className="text-xs font-black text-gray-500 uppercase flex-1">Lớp ({classes.length})</span>
            <button onClick={loadData} className="p-1.5 text-gray-400 hover:text-blue-600"><RefreshCw size={14} className={loading ? 'animate-spin' : ''} /></button>
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-1 max-h-[420px]">
            {loading && classes.length === 0 ? (
              <div className="py-10 text-center"><Loader2 className="animate-spin mx-auto text-blue-500" /></div>
            ) : error ? (
              <div className="py-10 px-4 text-center"><AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-3" /><p className="text-[11px] text-red-500 font-bold">{error}</p></div>
            ) : classes.length === 0 ? (
              <div className="py-10 px-4 text-center"><Layers className="w-10 h-10 text-gray-200 mx-auto mb-3" /><p className="text-[11px] text-gray-400 font-bold uppercase">Chưa có lớp. Nhập từ Excel.</p></div>
            ) : (
              classes.map((cls) => (
                <div key={cls.id} onClick={() => setSelectedClassName(cls.id)}
                  className={`group/item flex items-center justify-between p-3 rounded-xl cursor-pointer border ${selectedClassName === cls.id ? 'bg-blue-50 border-blue-200' : 'hover:bg-gray-50 border-transparent'}`}>
                  <div className="flex-1 min-w-0">
                    <h4 className={`font-black text-sm truncate ${selectedClassName === cls.id ? 'text-blue-700' : 'text-gray-800'}`}>{cls.name}</h4>
                    <p className="text-[10px] text-gray-500 font-bold uppercase">{cls.students} học sinh</p>
                  </div>
                  <button onClick={(e) => deleteClass(e, cls.name)} className="p-1.5 text-gray-300 hover:text-red-500 opacity-0 group-hover/item:opacity-100 rounded-lg hover:bg-red-50" title="Xoá lớp"><Trash2 size={14} /></button>
                  <ChevronRight size={16} className={selectedClassName === cls.id ? 'text-blue-500' : 'text-gray-300'} />
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Bảng học sinh của lớp */}
      <div className="flex-1 bg-white border border-gray-200 rounded-3xl shadow-sm overflow-hidden">
        {!activeClass ? (
          <div className="py-20 text-center text-gray-300">
            <Users size={48} className="mx-auto mb-4" />
            <p className="text-sm font-bold uppercase tracking-widest">Chọn một lớp để xem học sinh</p>
          </div>
        ) : (
          <>
            <div className="p-5 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-blue-50/50 to-white">
              <div>
                <h2 className="text-xl font-black text-gray-900">{activeClass.name}</h2>
                <p className="text-[10px] text-gray-500 font-bold uppercase tracking-widest">Sĩ số: <strong className="text-blue-600">{activeClass.students}</strong> học sinh</p>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className="bg-gray-50 border-b border-gray-100 text-[10px] text-gray-500 uppercase tracking-widest font-black">
                  <tr>
                    <th className="px-4 py-3 w-12 text-center">STT</th>
                    <th className="px-4 py-3">Học sinh / Email</th>
                    <th className="px-4 py-3">Trường</th>
                    <th className="px-4 py-3 text-center">Trạng thái</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredStudents.length === 0 ? (
                    <tr><td colSpan={4} className="px-4 py-10 text-center text-gray-400 font-medium">Chưa có học sinh.</td></tr>
                  ) : (
                    filteredStudents.map((hs, i) => (
                      <tr key={hs.id} className="hover:bg-gray-50/50">
                        <td className="px-4 py-3 text-center text-xs font-bold text-gray-400">{i + 1}</td>
                        <td className="px-4 py-3"><div className="font-black text-gray-900 text-sm">{hs.studentName || 'Chưa có tên'}</div><div className="text-[10px] text-gray-400 font-bold">{hs.studentEmail}</div></td>
                        <td className="px-4 py-3 text-xs font-bold text-gray-600 italic">{hs.school || '—'}</td>
                        <td className="px-4 py-3 text-center">
                          <span className={`inline-flex px-2.5 py-0.5 rounded-full text-[9px] font-black uppercase border ${hs.status === 'linked' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
                            {hs.status === 'linked' ? 'Đã tham gia' : 'Chờ đăng nhập'}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      <ImportStudentsModal isOpen={isImportModalOpen} onClose={() => setIsImportModalOpen(false)} onImport={loadData} />
    </div>
  )
}

export default StudentManager
